import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckSquare, Calendar, AlertCircle, Circle, Edit, Trash2, Plus } from "lucide-react";
import TaskDialog from "./TaskDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TasksListSection = ({ leadId, onTaskCreated }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTask, setEditingTask] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, [leadId, onTaskCreated]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/leads/${leadId}/tasks`, {
        withCredentials: true
      });
      setTasks(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      if (loading) {
        toast.error("Failed to fetch tasks");
      }
      setLoading(false);
    }
  };

  const toggleTaskStatus = async (task) => {
    try {
      const newStatus = task.status === "completed" ? "pending" : "completed";
      await axios.patch(`${API}/tasks/${task.id}`, {
        status: newStatus
      }, {
        withCredentials: true
      });
      fetchTasks();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;

    try {
      await axios.delete(`${API}/tasks/${taskId}`, {
        withCredentials: true
      });
      toast.success("Task deleted");
      fetchTasks();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "destructive";
      case "medium": return "default";
      case "low": return "secondary";
      default: return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No due date";
    return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CheckSquare className="w-5 h-5" />
            <span>Tasks</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-500 text-center py-4">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <CheckSquare className="w-5 h-5" />
              <span>Tasks ({tasks.length})</span>
            </CardTitle>
            <Button 
              size="sm" 
              onClick={() => {
                setEditingTask(null);
                setShowEditDialog(true);
              }}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              + Add Task
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {tasks.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500 text-sm mb-3">No tasks yet</p>
              <Button 
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingTask(null);
                  setShowEditDialog(true);
                }}
              >
                Create Your First Task
              </Button>
            </div>
          ) : (
            tasks.map((task) => (
              <div
                key={task.id}
                className={`p-3 border rounded-lg hover:bg-slate-50 transition-colors ${
                  task.status === "completed" ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-start space-x-3">
                  <Checkbox
                    checked={task.status === "completed"}
                    onCheckedChange={() => toggleTaskStatus(task)}
                    className="mt-1"
                  />
                  <div className="flex-1 space-y-1">
                    <div className="flex items-start justify-between">
                      <p className={`font-medium text-sm ${
                        task.status === "completed" ? "line-through text-slate-500" : "text-slate-900"
                      }`}>
                        {task.title}
                      </p>
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => {
                            setEditingTask(task);
                            setShowEditDialog(true);
                          }}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-red-500"
                          onClick={() => deleteTask(task.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    {task.description && (
                      <p className="text-xs text-slate-600">{task.description}</p>
                    )}
                    <div className="flex items-center space-x-2 text-xs">
                      {task.due_date && (
                        <span className="flex items-center space-x-1 text-slate-500">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(task.due_date)}</span>
                        </span>
                      )}
                      <Badge variant={getPriorityColor(task.priority)} className="text-xs h-5">
                        {task.priority}
                      </Badge>
                      {task.status === "completed" && (
                        <Badge variant="outline" className="text-xs h-5">Completed</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {showEditDialog && (
        <TaskDialog
          open={showEditDialog}
          onClose={() => {
            setShowEditDialog(false);
            setEditingTask(null);
          }}
          onSuccess={() => {
            setShowEditDialog(false);
            setEditingTask(null);
            fetchTasks();
          }}
          task={editingTask}
          leadId={leadId}
        />
      )}
    </>
  );
};

export default TasksListSection;
